import React from "react";
import { mergeOverrides } from "baseui";
import {
  StatefulTooltip as BaseUITooltip,
  StatefulTooltipProps as BaseTooltipProps,
  ACCESSIBILITY_TYPE,
  PLACEMENT,
  TRIGGER_TYPE,
} from "baseui/tooltip";
import { Overrides } from "baseui/overrides";
import { PopoverOverrides } from "baseui/popover";
import { SIZE } from "baseui/button";
import styled from "styled-components";
import { Button, ButtonProps } from "../../inputs/button";
import { Theme } from "../../../../styling/baseui_theme";
import { InformationFilled } from "@carbon/icons-react";

const defaultStyleOverrides: Overrides<PopoverOverrides> = {
  Body: {
    style: ({ $theme }: { $theme: Theme }) => ({
      borderTopLeftRadius: "5px",
      borderTopRightRadius: "5px",
      borderBottomRightRadius: "5px",
      borderBottomLeftRadius: "5px",
      maxWidth: "300px",
      zIndex: $theme.zIndex.modal + 3,
    }),
  },
  Inner: {
    style: ({ $theme }: { $theme: Theme }) => ({
      borderTopLeftRadius: "5px",
      borderTopRightRadius: "5px",
      borderBottomRightRadius: "5px",
      borderBottomLeftRadius: "5px",
      backgroundColor: $theme.colors.mono1000,
      ...$theme.typography.font200,
      paddingLeft: "16px",
      paddingTop: "16px",
      paddingRight: "16px",
      paddingBottom: "16px",
      zIndex: $theme.zIndex.modal + 3,
    }),
  },
  Arrow: {
    style: ({ $theme }: { $theme: Theme }) => ({
      backgroundColor: $theme.colors.mono1000,
      zIndex: $theme.zIndex.modal + 3,
    }),
  },
};

export const StyledInformationFilled = styled(InformationFilled)`
  fill: ${({ theme }) => theme.colors.contentPrimary};
`;

export const TooltipContent = styled.div`
  font-family: ${({ theme }) => theme.typography.font200.fontFamily};
  font-size: ${({ theme }) => theme.typography.font200.fontSize};
  font-weight: ${({ theme }) => theme.typography.font200.fontWeight};
`;

const TooltipAnchor = styled.span`
  &:focus,
  &:focus-visible {
    outline-color: ${({ theme }) => theme.colors.accent};
  }
`;

export interface TooltipProps extends BaseTooltipProps {
  styletronOverrides?: PopoverOverrides;
  dataAnalyticsId?: string;
}

export const Tooltip = ({
  content,
  accessibilityType = ACCESSIBILITY_TYPE.tooltip,
  placement = PLACEMENT.auto,
  triggerType = TRIGGER_TYPE.hover,
  showArrow = true,
  popoverMargin = 8,
  styletronOverrides,
  children,
  dataAnalyticsId,
  ...props
}: TooltipProps) => {
  let styleOverride = defaultStyleOverrides;

  if (styletronOverrides) {
    styleOverride = mergeOverrides(
      styletronOverrides as Overrides<PopoverOverrides>,
      defaultStyleOverrides,
    );
  }

  return (
    <BaseUITooltip
      // base ui types aren't playing well with react types
      // eslint-disable-next-line
      // @ts-ignore-next-line
      content={<TooltipContent>{content}</TooltipContent>}
      accessibilityType={accessibilityType}
      placement={placement}
      triggerType={triggerType}
      showArrow={showArrow}
      popoverMargin={popoverMargin}
      overrides={styleOverride}
      {...props}
    >
      {/* tabIndex provides keyboard accessibility */}
      <TooltipAnchor
        tabIndex={0}
        data-analytics-id={dataAnalyticsId ? `${dataAnalyticsId}-tooltip` : undefined}
      >
        {children}
      </TooltipAnchor>
    </BaseUITooltip>
  );
};

export type TooltipLabelProps = Omit<BaseTooltipProps, "content"> & {
  primaryContent: BaseTooltipProps["content"];
  secondaryContent: BaseTooltipProps["content"];
};

const PrimaryContent = styled.p`
  font-family: ${({ theme }) => theme.typography.font400.fontFamily};
  font-size: ${({ theme }) => theme.typography.font400.fontSize};
  font-weight: ${({ theme }) => theme.typography.font550.fontWeight};
  line-height: 100%;
  margin: 2px 0px 0px 0px;
`;

const SecondaryContent = styled.p`
  font-family: ${({ theme }) => theme.typography.font100.fontFamily};
  font-size: ${({ theme }) => theme.typography.font100.fontSize};
  font-weight: ${({ theme }) => theme.typography.font100.fontWeight};
  line-height: 100%;
  margin: 0px 0px 2px 0px;
`;

export const TooltipLabel = ({
  primaryContent,
  secondaryContent,
  accessibilityType,
  placement,
  triggerType,
  showArrow,
  popoverMargin,
  children,
  ...props
}: TooltipLabelProps) => {
  const content = (
    <>
      {/* base ui types aren't playing well with react types */}
      {/* eslint-disable-next-line */}
      {/* @ts-ignore-next-line */}
      <PrimaryContent>{primaryContent}</PrimaryContent>
      {/* base ui types aren't playing well with react types */}
      {/* eslint-disable-next-line */}
      {/* @ts-ignore-next-line */}
      <SecondaryContent>{secondaryContent}</SecondaryContent>
    </>
  );

  return (
    <Tooltip
      content={content}
      accessibilityType={accessibilityType}
      placement={placement}
      triggerType={triggerType}
      showArrow={showArrow}
      popoverMargin={popoverMargin}
      {...props}
    >
      {children}
    </Tooltip>
  );
};

export type TooltipWithButtonIconProps = Omit<BaseTooltipProps, "content"> & {
  content?: React.ReactNode;
  buttonIcon?: ButtonProps["rightIcon"];
  buttonOnClick?: () => void;
  button?: ButtonProps;
};

const withIconStyleOverrides: PopoverOverrides = {
  Inner: {
    style: () => ({
      borderTopLeftRadius: "5px",
      borderTopRightRadius: "5px",
      borderBottomRightRadius: "5px",
      borderBottomLeftRadius: "5px",
      paddingLeft: "16px",
      paddingTop: "6px",
      paddingRight: "6px",
      paddingBottom: "6px",
    }),
  },
  Body: {
    style: () => ({
      borderTopLeftRadius: "5px",
      borderTopRightRadius: "5px",
      borderBottomRightRadius: "5px",
      borderBottomLeftRadius: "5px",
    }),
  },
};

const ContentWithButtonIcon = styled.div`
  display: flex;
  align-items: center;

  .tooltip-button-icon {
    background-color: transparent;
    margin-left: 6px;
    padding: 0px;
    width: 36px;
    height: 36px;

    &:hover {
      background-color: ${({ theme }) => theme.colors.contentSecondary};
    }
  }
`;

export const TooltipWithButtonIcon = ({
  content,
  accessibilityType,
  placement,
  triggerType,
  showArrow,
  popoverMargin,
  buttonIcon,
  buttonOnClick,
  button,
  children,
  ...props
}: TooltipWithButtonIconProps) => {
  content = (
    <ContentWithButtonIcon>
      {/* eslint-disable-next-line */}
      {/* @ts-ignore base ui types aren't playing well with react types */}
      <>
        {content}
        {button || (
          <Button className="tooltip-button-icon" size={SIZE.mini} onClick={buttonOnClick}>
            {/* base ui types aren't playing well with react types */}
            {/* eslint-disable-next-line */}
            {/* @ts-ignore-next-line */}
            {buttonIcon}
          </Button>
        )}
      </>
    </ContentWithButtonIcon>
  );

  return (
    <Tooltip
      content={content}
      accessibilityType={accessibilityType}
      placement={placement}
      triggerType={triggerType}
      showArrow={showArrow}
      popoverMargin={popoverMargin}
      overrides={withIconStyleOverrides}
      {...props}
    >
      {children}
    </Tooltip>
  );
};
